<template>
  <UDropdownMenu
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{ content: 'min-w-[220px] w-[240px] max-w-[90vw]' }"
  >
    <UTooltip
      :text="collapsed && !props.disableTooltip ? tooltipText : undefined"
      :disabled="!collapsed || props.disableTooltip"
      :popper="{ placement: 'right', offsetDistance: 10 }"
    >
      <UButton
        :trailingIcon="collapsed ? undefined : 'i-lucide-chevrons-up-down'"
        color="neutral"
        variant="ghost"
        block
        :square="collapsed"
        :loading="isAuthLoading"
        class="data-[state=open]:bg-bg-elevated"
        :ui="{
          trailingIcon: 'text-dimmed'
        }"
      >
        <template #leading>
          <div class="shrink-0">
            <NameAvatar :fullName="displayName" :size="25" customColor="#C8017D" />
          </div>
        </template>
        
        <div v-if="!collapsed" class="flex flex-col items-start text-left">
          <span :class="isAuthenticated ? 'text-xs font-medium' : 'text-xs text-dimmed'">
            {{ displayName }}
          </span>
          <span v-if="isAuthenticated && roleLabel" class="text-xs text-gray-500 dark:text-gray-400 whitespace-normal break-words">
            {{ roleLabel }}
          </span>
        </div>
      </UButton>
    </UTooltip>

    <template #chip-leading="{ item }">
      <span 
        :style="{
          '--chip-light': `var(--color-${(item as any).chip}-500)`,
          '--chip-dark': `var(--color-${(item as any).chip}-400)`
        }"
          class="ms-0.5 size-2 rounded-full bg-(--chip-light) dark:bg-(--chip-dark)"
      />
    </template>
  </UDropdownMenu>
</template>

<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import { ref, computed, watch } from 'vue'
import { useColorMode, useAppConfig } from '#imports'
import { useGlobal, useUser } from '@/store';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  collapsed?: boolean
  disableTooltip?: boolean
}>()

const { t } = useI18n()

const colorMode = useColorMode()
const appConfig = useAppConfig()
const userStore = useUser();

const isAuthLoading = ref(false)
const isAuthenticated = computed(() => userStore.isAuthenticated())
const displayName = computed(() => isAuthenticated.value ? userStore.fullName : t('userMenu.auth.notLoggedIn'))
const userEmail = computed(() => isAuthenticated.value ? userStore.user.email : '')

const roleLabel = computed(() => {
  if (!isAuthenticated.value) {
    return ''
  }
  
  const roles = userStore.getUserRoles()
  
  if (roles.length === 0) {
    return ''
  }
  
  return roles.map(role => role.replace('role.', '').replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).join(', ')
})

const tooltipText = computed(() => {
  if (!isAuthenticated.value) {
    return t('userMenu.auth.notLoggedIn')
  }
  
  if (roleLabel.value) {
    return `${displayName.value} (${roleLabel.value})`
  }
  
  return displayName.value
})

const items = computed<DropdownMenuItem[][]>(() => {
  const sections: DropdownMenuItem[][] = []
  
  if (isAuthenticated.value) {
    const userInfo: DropdownMenuItem[] = [
      {
        label: displayName.value,
        icon: 'i-lucide-user',
        disabled: true,
        class: 'cursor-default whitespace-normal break-words'
      }
    ]
    
    if (userEmail.value) {
      userInfo.push({
        label: userEmail.value,
        icon: 'i-lucide-mail',
        disabled: true,
        class: 'cursor-default text-gray-600 dark:text-gray-300',
        ui: {
          label: 'truncate'
        }
      })
    }

    if (roleLabel.value) {
      userInfo.push({
        label: roleLabel.value,
        icon: 'i-lucide-shield-check',
        disabled: true,
        class: 'cursor-default whitespace-normal break-words text-gray-600 dark:text-gray-300'
      })
    }
    
    sections.push(userInfo)
    
    /*
    sections.push([{
      label: t('userMenu.settings'),
      icon: 'i-lucide-settings',
      to: '/settings'
    }])
    */
  }
  
  sections.push([{
    label: t('userMenu.appearance.label'),
    icon: 'i-lucide-sun-moon',
    children: [{
      label: t('userMenu.appearance.modes.light'),
      icon: 'i-lucide-sun',
      type: 'checkbox',
      checked: colorMode.value === 'light',
      onUpdateChecked(checked: boolean) {
        if (checked) {
          colorMode.preference = 'light'
        }
      },
      onSelect(e: Event) {
        e.preventDefault()
        colorMode.preference = 'light'
      }
    }, {
      label: t('userMenu.appearance.modes.dark'),
      icon: 'i-lucide-moon',
      type: 'checkbox',
      checked: colorMode.value === 'dark',
      onUpdateChecked(checked: boolean) {
        if (checked) {
          colorMode.preference = 'dark'
        }
      },
      onSelect(e: Event) {
        e.preventDefault()
        colorMode.preference = 'dark'
      }
    }]
  }])
  
  sections.push([{
    label: isAuthenticated.value ? t('userMenu.auth.logout') : t('userMenu.auth.login'),
    icon: isAuthenticated.value ? 'i-lucide-log-out' : 'i-lucide-log-in',
    color: isAuthenticated.value ? 'error' : undefined,
    disabled: isAuthLoading.value,
    onSelect(e: Event) {
      isAuthenticated.value ? logoutClicked() : loginClicked()
    }
  }])
  
  return sections
})

async function logoutClicked() {
  if (isAuthLoading.value) {
    return
  }
  
  isAuthLoading.value = true
  
  try {
    await userStore.logout();
  } finally {
    isAuthLoading.value = false
  }
}

async function loginClicked() {
  if (isAuthLoading.value) {
    return
  }
  
  isAuthLoading.value = true
  
  try {
    await userStore.login();
  } finally {
    isAuthLoading.value = false
  }
}
</script>